import { onDestroy } from "@/modules/_";
import gsap, { CustomWiggle } from "@lib/gsap";
import { gsapEase } from "@/utils/easings";

CustomWiggle.create("brandWiggle", { wiggles: 2, type: "easeOut" });

const IN_DURATION = 0.3;
const IN_EASE = gsapEase("pop");
const OUT_DURATION = 0.3;
const OUT_EASE = gsapEase("osmo");
const FROM_SCALE = 0.4;
const TRAVEL = 0.3;

const XY_MULT = 12;
const XY_CLAMP = 60;
const INERTIA_RESISTANCE = 200;

const WIGGLE_MULT = 0.6;
const WIGGLE_AMP_CLAMP = 12;
const WIGGLE_AMP_MIN = 4;
const WIGGLE_DURATION = 0.5;

const PEEK_MS = 1500;

export default function (element: HTMLElement, _dataset: DOMStringMap) {
  const spans = Array.from(
    element.querySelectorAll<HTMLElement>('[inline-brand="span"]'),
  );
  if (spans.length === 0) return;

  const isHover =
    typeof window !== "undefined" &&
    window.matchMedia("(hover: hover) and (pointer: fine)").matches;

  type Entry = { logo: HTMLElement; cleanup: () => void };
  const entries: Entry[] = [];

  let prevX = 0;
  let prevY = 0;
  let velX = 0;
  let velY = 0;
  let rafId: number | null = null;

  const onMove = (e: MouseEvent) => {
    if (rafId !== null) return;
    rafId = requestAnimationFrame(() => {
      velX = e.clientX - prevX;
      velY = e.clientY - prevY;
      prevX = e.clientX;
      prevY = e.clientY;
      rafId = null;
    });
  };
  if (isHover) element.addEventListener("mousemove", onMove);

  const clampXY = gsap.utils.clamp(-XY_CLAMP, XY_CLAMP);
  const clampWiggle = gsap.utils.clamp(-WIGGLE_AMP_CLAMP, WIGGLE_AMP_CLAMP);

  spans.forEach((span) => {
    const text = span.querySelector<HTMLElement>(
      ':scope > span:not([inline-brand="logo"])',
    );
    const logo = span.querySelector<HTMLElement>('[inline-brand="logo"]');
    if (!text || !logo) return;

    // scale 0 hides visually but keeps the inline box, so text never reflows.
    gsap.set(logo, {
      opacity: 0,
      scale: 0,
      transformOrigin: "center center",
    });

    const getDelta = () => {
      const dx =
        (text.offsetLeft +
          text.offsetWidth / 2 -
          (logo.offsetLeft + logo.offsetWidth / 2)) *
        TRAVEL;
      const dy =
        (text.offsetTop +
          text.offsetHeight / 2 -
          (logo.offsetTop + logo.offsetHeight / 2)) *
        TRAVEL;
      return { dx, dy };
    };

    const onEnter = () => {
      const { dx, dy } = getDelta();

      gsap.set(logo, {
        x: dx,
        y: dy,
        rotation: 0,
        scale: FROM_SCALE,
        opacity: 0,
      });

      gsap.to(logo, {
        scale: 1,
        opacity: 1,
        duration: IN_DURATION,
        ease: IN_EASE,
        overwrite: "auto",
      });

      gsap.to(logo, {
        inertia: {
          x: { velocity: clampXY(velX * XY_MULT), end: 0 },
          y: { velocity: clampXY(velY * XY_MULT), end: 0 },
          resistance: INERTIA_RESISTANCE,
        },
        overwrite: "auto",
      });

      const speed = Math.hypot(velX, velY);
      const dir = velX >= 0 ? 1 : -1;
      const amp = clampWiggle(
        dir * Math.max(WIGGLE_AMP_MIN, speed * WIGGLE_MULT),
      );
      gsap.fromTo(
        logo,
        { rotation: amp },
        {
          rotation: 0,
          duration: WIGGLE_DURATION,
          ease: "brandWiggle",
          overwrite: "auto",
        },
      );
    };

    const onLeave = () => {
      const { dx, dy } = getDelta();
      gsap.to(logo, {
        x: dx,
        y: dy,
        rotation: 0,
        opacity: 0,
        scale: FROM_SCALE,
        duration: OUT_DURATION,
        ease: OUT_EASE,
        overwrite: "auto",
      });
    };

    if (isHover) {
      span.addEventListener("mouseenter", onEnter);
      span.addEventListener("mouseleave", onLeave);
      entries.push({
        logo,
        cleanup: () => {
          span.removeEventListener("mouseenter", onEnter);
          span.removeEventListener("mouseleave", onLeave);
        },
      });
    } else {
      let timer: number | null = null;
      const onTap = () => {
        if (timer !== null) clearTimeout(timer);
        onEnter();
        timer = window.setTimeout(() => {
          onLeave();
          timer = null;
        }, PEEK_MS);
      };
      span.addEventListener("click", onTap);
      entries.push({
        logo,
        cleanup: () => {
          if (timer !== null) clearTimeout(timer);
          span.removeEventListener("click", onTap);
        },
      });
    }
  });

  onDestroy(() => {
    if (isHover) element.removeEventListener("mousemove", onMove);
    if (rafId !== null) cancelAnimationFrame(rafId);
    entries.forEach(({ logo, cleanup }) => {
      cleanup();
      gsap.killTweensOf(logo);
      gsap.set(logo, {
        clearProps: "transform,scale,opacity,x,y,rotation",
      });
    });
    entries.length = 0;
  });
}
